import { useMemo } from 'react';
import { useLanguage } from '@/shared/i18n';

// Number formats from the production KPI catalog (helpers/formatting.ts) and the prototype.
//
// Figures are shown as they arrive, never rounded off for display: money always carries its
// cents, counts keep any fractional part, and shares keep a second decimal when they have one.
// `currency` and `currency-precise` therefore render identically; both names are kept because
// KPI_FORMAT and the export columns are typed against them.
export type NumberFormat = 'currency' | 'currency-precise' | 'number' | 'decimal' | 'percent';

const NNBSP = ' ';

export function createFormatters(locale: string) {
  const eur0 = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  const eur2 = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const int = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 });
  const upTo2 = new Intl.NumberFormat(locale, { maximumFractionDigits: 2 });
  const dec1 = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });
  /** One decimal minimum so a column stays aligned, a second one when the value has it. */
  const share = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 2,
  });
  const isDe = locale.startsWith('de');

  const valid = (v: number | null | undefined): v is number =>
    typeof v === 'number' && Number.isFinite(v);

  const fmt = {
    currency: (v: number | null | undefined) => (valid(v) ? eur2.format(v) : '—'),
    currencyPrecise: (v: number | null | undefined) => (valid(v) ? eur2.format(v) : '—'),
    /** Counts, kept whole when they are whole and fractional when they are not. */
    number: (v: number | null | undefined) => (valid(v) ? upTo2.format(v) : '—'),
    /** Up to two decimals, no padding (raw measures such as utilization times). */
    plain: (v: number | null | undefined) => (valid(v) ? upTo2.format(v) : '—'),
    decimal: (v: number | null | undefined, digits = 1) =>
      valid(v)
        ? new Intl.NumberFormat(locale, {
            minimumFractionDigits: digits,
            maximumFractionDigits: digits,
          }).format(v)
        : '—',
    /** 12,3 % (de) / 12.34% (en) — value already in percent. */
    percent: (v: number | null | undefined) =>
      valid(v) ? (isDe ? `${share.format(v)}${NNBSP}%` : `${share.format(v)}%`) : '—',
    /** Signed percentage points: +1,2 pp. */
    points: (v: number | null | undefined) =>
      valid(v) ? `${v > 0 ? '+' : v < 0 ? '−' : ''}${share.format(Math.abs(v))}${NNBSP}pp` : '—',
    /** Signed percent change: +4,2 %. */
    change: (v: number | null | undefined) =>
      valid(v)
        ? `${v > 0 ? '+' : v < 0 ? '−' : ''}${isDe ? `${share.format(Math.abs(v))}${NNBSP}%` : `${share.format(Math.abs(v))}%`}`
        : '—',
    /** Short axis labels: 12k €. */
    compactCurrency: (v: number | null | undefined) => {
      if (!valid(v)) return '—';
      const k = v / 1000;
      if (Math.abs(k) < 1) return eur0.format(Math.round(v));
      const text = Number.isInteger(Math.round(k * 10) / 10)
        ? int.format(Math.round(k))
        : dec1.format(k);
      return `${text}k${NNBSP}€`;
    },
    by: (format: NumberFormat, v: number | null | undefined) => {
      switch (format) {
        case 'currency':
          return fmt.currency(v);
        case 'currency-precise':
          return fmt.currencyPrecise(v);
        case 'decimal':
          return fmt.decimal(v);
        case 'percent':
          return fmt.percent(v);
        default:
          return fmt.number(v);
      }
    },
  };
  return fmt;
}

export type Formatters = ReturnType<typeof createFormatters>;

/** Formatters bound to the current UI language. */
export function useFormatters(): Formatters {
  const { locale } = useLanguage();
  return useMemo(() => createFormatters(locale), [locale]);
}
